const TIER_PRESENTATIONS = {
  non: {
    key: 'non',
    name: 'Non-polarized',
    positioning: 'Clear everyday replacement',
    bestFor: 'Everyday outdoor use',
    benefit: 'A familiar, balanced view',
    colorNote: 'Available non-polarized colors',
    valueText: 'Non-polarized lens simulation',
  },
  polarized: {
    key: 'polarized',
    name: 'Polarized',
    positioning: 'Cuts reflected glare',
    bestFor: 'Roads, water and bright surfaces',
    benefit: 'More comfort around reflected glare',
    colorNote: 'Available polarized colors',
    valueText: 'Polarized lens simulation',
  },
  clarity: {
    key: 'clarity',
    name: 'Premium Polarized',
    positioning: 'Clear visual detail',
    bestFor: 'Detail-focused everyday use',
    benefit: 'Clarity and color definition',
    colorNote: 'Full listed color range',
    valueText: 'Premium Polarized lens simulation',
  },
};

function normalizeLensTier(value) {
  const normalized = String(value || '').trim().toLowerCase().replace(/[^a-z]+/g, '-');
  const compact = normalized.replace(/-/g, '');
  if (compact === 'bestclarity' || compact === 'premiumpolarized' || normalized === 'clarity') return 'clarity';
  if (normalized === 'non') return 'non';
  if (normalized.includes('non-polarized')) return 'non';
  if (normalized.includes('polarized')) return 'polarized';
  return '';
}

function getTierPresentation(value) {
  const key = normalizeLensTier(value);
  return key ? { ...TIER_PRESENTATIONS[key] } : null;
}

function clampSplitPosition(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 62;
  return Math.min(82, Math.max(18, numeric));
}

function initLensComparison(root) {
  const comparison = root?.querySelector?.('[data-lens-comparison]');
  if (!comparison || comparison.dataset.comparisonReady === 'true') return null;
  comparison.dataset.comparisonReady = 'true';

  const visual = comparison.querySelector('[data-comparison-visual]');
  const range = comparison.querySelector('[data-comparison-range]');
  const tierButtons = [...comparison.querySelectorAll('[data-comparison-tier]')];
  const tierInputs = [...root.querySelectorAll('.mry-pdp__option--tier:not([data-option-kind="grade"]) [data-option-input]')];
  const chooseButton = comparison.querySelector('[data-comparison-choose]');
  const output = {
    name: comparison.querySelector('[data-comparison-name]'),
    positioning: comparison.querySelector('[data-comparison-positioning]'),
    bestFor: comparison.querySelector('[data-comparison-best-for]'),
    benefit: comparison.querySelector('[data-comparison-benefit]'),
    colorNote: comparison.querySelector('[data-comparison-color-note]'),
  };
  let selectedKey = comparison.dataset.comparisonTierKey || 'polarized';

  const renderSplit = (value) => {
    const position = clampSplitPosition(value);
    visual?.style.setProperty('--comparison-split', `${position}%`);
    if (range) {
      range.value = String(position);
      range.setAttribute('aria-valuetext', `${position} percent through lens`);
    }
  };

  const renderTier = (value) => {
    const presentation = getTierPresentation(value);
    if (!presentation) return;
    selectedKey = presentation.key;
    comparison.dataset.comparisonTierKey = presentation.key;
    tierButtons.forEach((button) => {
      const selected = button.dataset.comparisonTier === presentation.key;
      button.setAttribute('aria-checked', String(selected));
      button.tabIndex = selected ? 0 : -1;
    });
    Object.entries(output).forEach(([field, element]) => {
      if (element) element.textContent = presentation[field];
    });
    if (visual) visual.setAttribute('aria-label', presentation.valueText);
  };

  const findTierInput = (key) => tierInputs.find((input) => normalizeLensTier(input.value) === key);

  const chooseTier = (key, moveToOptions = false) => {
    const input = findTierInput(key);
    if (!input || input.disabled) return false;
    if (!input.checked) input.click();
    renderTier(input.value);
    if (moveToOptions) {
      const tierGroup = input.closest('.mry-pdp__option--tier');
      tierGroup?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center',
      });
      window.requestAnimationFrame(() => input.focus({ preventScroll: true }));
    }
    return true;
  };

  const selectedInput = tierInputs.find((input) => input.checked);
  renderTier(selectedInput?.value || selectedKey);
  renderSplit(range?.value || 62);

  range?.addEventListener('input', (event) => renderSplit(event.target.value));
  range?.addEventListener('change', (event) => renderSplit(event.target.value));

  tierButtons.forEach((button, index) => {
    button.addEventListener('click', () => chooseTier(button.dataset.comparisonTier));
    button.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      let nextIndex = index;
      if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = tierButtons.length - 1;
      else nextIndex = (index + (event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1) + tierButtons.length) % tierButtons.length;
      const nextButton = tierButtons[nextIndex];
      if (chooseTier(nextButton.dataset.comparisonTier)) nextButton.focus();
    });
  });

  chooseButton?.addEventListener('click', () => chooseTier(selectedKey, true));
  root.addEventListener('change', (event) => {
    if (event.target.matches('.mry-pdp__option--tier [data-option-input]')) renderTier(event.target.value);
  });

  return { chooseTier, renderSplit, renderTier };
}

function initAllLensComparisons(scope) {
  return [...scope.querySelectorAll('[data-mry-product]')].map(initLensComparison).filter(Boolean);
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    clampSplitPosition,
    getTierPresentation,
    initLensComparison,
    normalizeLensTier,
  };
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  window.MRYLensComparison = {
    clampSplitPosition,
    getTierPresentation,
    initLensComparison,
    normalizeLensTier,
  };
  const initialize = () => initAllLensComparisons(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
}
